import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Breadcrumb } from '@/components/ui/breadcrumb';
import { CaretLeftIcon, CaretRightIcon, WarningCircleIcon } from '@/components/icons';
import Spinner from '@/components/ui/spinner';
import { StatusCard } from '@/components/StatusCard';
import { useI18n } from '@/i18n/I18nProvider';
import { useToastNotifications } from '@/lib/hooks/use-toast-notifications';
import { useGetStudentById } from '@/lib/hooks/useGetStudentById';
import { useGetCharter } from '@/lib/hooks/useGetCharter';

import { SchoolInfoCard } from './components/SchoolInfoCard';
import { ParentInfoCard } from './components/ParentInfoCard';
import { CharterTermsView } from './components/CharterTermsView';
import { CharterSignatureView } from './components/CharterSignatureView';
import { CharterSignedView } from './components/CharterSignedView';

const PLACEHOLDER = '—';

export default function ParentConductPage() {
  const { t, locale } = useI18n();
  const toast = useToastNotifications();
  const params = useParams<{ id: string }>();
  const studentId = params.id || '';

  const [currentStep, setCurrentStep] = React.useState(1);
  const [canProceedWithIDH, setCanProceedWithIDH] = React.useState(true);

  const { student, error: studentError, isLoading: isStudentLoading, mutate: mutateStudent } = useGetStudentById(studentId);
  const studentNumber = student?.studentNumber?.trim() ?? null;

  const {
    charterRecord,
    isSigned,
    isLoading: isLoadingCharter,
    mutateCharter,
  } = useGetCharter(studentNumber, undefined, locale);

  const schoolName = charterRecord?.schoolName || PLACEHOLDER;
  const schoolYearLabel = charterRecord?.schoolYear || PLACEHOLDER;
  const studentFullName = charterRecord?.studentName || PLACEHOLDER;
  const parentFullName = charterRecord?.parentName || PLACEHOLDER;

  const isExpatCitizenship = React.useMemo(() => {
    const c = (student?.CitizenshipStatus ?? '').trim().toLowerCase();
    return c === 'expat arab' || c === 'expat non arab';
  }, [student?.CitizenshipStatus]);

  const handleNext = () => {
    if (currentStep === 2 && !canProceedWithIDH) {
      toast.error(
        locale === 'ar' ? 'خطأ في الحالة' : 'Status Error',
        locale === 'ar'
          ? 'لا يمكن المتابعة. يجب أن تكون حالة IDH "مكتمل" (4) للمتابعة لتوقيع الميثاق.'
          : 'Cannot proceed. IDH status must be "Complete" (4) to continue signing the charter.'
      );
      return;
    }
    if (currentStep < 4) setCurrentStep(currentStep + 1);
  };

  const handlePrevious = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  if (isStudentLoading || (studentNumber && isLoadingCharter)) {
    return <Spinner />;
  }

  if (studentError || !student) {
    return <StatusCard message={studentError?.title} description={t.child.no_data_available_for_child} />;
  }

  return (
    <div className="py-6" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
      {/* Top nav */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <Breadcrumb
          items={[
            { label: locale === 'ar' ? 'الرئيسية' : 'Home', href: '/dashboard' },
            {
              label: locale === 'ar' ? 'عرض الملف' : 'View Profile',
              href: studentId ? `/students/${encodeURIComponent(studentId)}` : '/dashboard',
            },
            { label: locale === 'ar' ? 'ميثاق الشراكة' : t.parentConduct.title || 'Parent Conduct Charter' },
          ]}
        />
        {!isSigned && (
          <div className="text-xs text-muted-foreground">
            <span>
              {t.parentConduct.progressLabels.current} {currentStep} {t.parentConduct.progressLabels.of} 4
            </span>
          </div>
        )}
      </div>

      {isSigned ? (
        <CharterSignedView
          studentId={studentId}
          studentNumber={studentNumber}
          studentFullName={studentFullName}
          parentFullName={parentFullName}
          schoolName={schoolName}
          schoolYearLabel={schoolYearLabel}
        />
      ) : (
        <>
          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-foreground">{t.parentConduct.progress}</span>
              <span className="text-sm text-muted-foreground">{Math.round((currentStep / 4) * 100)}%</span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div
                className="bg-primary h-2 rounded-full transition-all duration-300 ease-in-out"
                style={{ width: `${(currentStep / 4) * 100}%` }}
              ></div>
            </div>
            <div className="flex justify-between mt-2 text-xs text-muted-foreground">
              <span className={currentStep >= 1 ? 'text-primary font-medium' : ''}>
                {t.parentConduct.steps.schoolInfo}
              </span>
              <span className={currentStep >= 2 ? 'text-primary font-medium' : ''}>
                {t.parentConduct.steps.parentInfo}
              </span>
              <span className={currentStep >= 3 ? 'text-primary font-medium' : ''}>{t.parentConduct.steps.terms}</span>
              <span className={currentStep >= 4 ? 'text-primary font-medium' : ''}>
                {t.parentConduct.steps.signature}
              </span>
            </div>
          </div>

          {/* Header */}
          <Card className="mb-8 border shadow-sm">
            <CardHeader className="rounded-t-xl">
              <CardTitle className="text-lg sm:text-xl font-semibold">{t.parentConduct.title}</CardTitle>
              <p className="mt-2 text-sm">{t.parentConduct.subtitle}</p>
            </CardHeader>
            <CardContent className="text-foreground leading-6 p-0">
              <div role="alert" className="aegov-alert alert-warning rounded-xl">
                <div className="alert-content">
                  <p className="font-medium text-foreground pb-3 flex items-center gap-2">
                    <WarningCircleIcon />
                    {t.parentConduct.introduction.title}
                  </p>
                  <p className="pb-3 text-muted-foreground text-sm">{t.parentConduct.introduction.paragraph1}</p>
                  <p className="text-muted-foreground text-sm">{t.parentConduct.introduction.paragraph2}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Wizard Steps */}
          {currentStep === 1 && (
            <SchoolInfoCard
              schoolName={charterRecord?.schoolName}
              schoolYear={charterRecord?.schoolYear}
              schoolAddress={charterRecord?.schoolAddress}
              schoolPhone={charterRecord?.schoolPhone}
              schoolEmail={charterRecord?.schoolEmail}
            />
          )}

          {currentStep === 2 && (
            <ParentInfoCard
              studentId={studentId}
              charterRecord={charterRecord}
              onCanProceedChange={setCanProceedWithIDH}
              onDataSynced={async () => {
                await mutateCharter();
              }}
            />
          )}

          {currentStep === 3 && <CharterTermsView isExpatCitizenship={isExpatCitizenship} />}

          {currentStep === 4 && (
            <CharterSignatureView
              studentNumber={studentNumber}
              academicYear={charterRecord?.schoolYear || schoolYearLabel}
              schoolName={schoolName}
              studentFullName={studentFullName}
              parentFullName={parentFullName}
              isSigned={isSigned}
              onSignedSuccess={() => {
                void mutateCharter();
              }}
            />
          )}

          {/* Step Navigation */}
          <div className="flex items-center justify-between gap-4 pt-6 border-t mt-8">
            <div className="flex gap-3">
              {currentStep > 1 && (
                <button type="button" className="aegov-btn btn-soft btn-secondary btn-sm" onClick={handlePrevious}>
                  {locale === 'ar' ? <CaretRightIcon size={20} /> : <CaretLeftIcon size={20} />}
                  {t.back}
                </button>
              )}
              <Link to={studentId ? `/students/${encodeURIComponent(studentId)}` : '/dashboard'}>
                <button className="aegov-btn btn-soft btn-secondary btn-sm" type="button">
                  {t.parentConduct.navigation.close}
                </button>
              </Link>
            </div>
            <div className="flex gap-3">
              {currentStep < 4 && (
                <button
                  type="button"
                  className="aegov-btn btn-sm"
                  onClick={handleNext}
                  disabled={currentStep === 2 && !canProceedWithIDH}
                >
                  {t.parentConduct.navigation.next}
                  {locale === 'ar' ? <CaretLeftIcon size={20} /> : <CaretRightIcon size={20} />}
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
